import React, { useState } from 'react';
import { X, Heart, DollarSign, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { mockEvents } from '@/lib/mockData';

const tipAmounts = [5, 10, 25, 50, 100];

export function TipArtistModal({ isOpen, onClose, artistId, eventId }) {
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [message, setMessage] = useState('');

  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];
  const artist = event?.artists?.find(a => a.id === artistId) || event?.artists?.[0];

  if (!isOpen) return null;

  const finalAmount = selectedAmount || (customAmount ? parseFloat(customAmount) : 0);

  const handleSendTip = () => {
    // Mock tip action
    alert(`Tip of $${finalAmount} sent to ${artist?.name}!`);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal - Bottom sheet on mobile, centered modal on desktop */}
      <Card className="relative z-10 w-full md:max-w-md md:mx-4 bg-card border-border rounded-t-3xl md:rounded-xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <h2 className="text-xl font-bold text-white">Tip Artist</h2>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Artist Info */}
          {artist && (
            <div className="flex flex-col items-center text-center">
              <div className="w-20 h-20 rounded-full overflow-hidden border-4 border-primary mb-3">
                <img 
                  src={artist.image} 
                  alt={artist.name}
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="text-lg font-semibold text-white">{artist.name}</h3>
              {event && <p className="text-sm text-muted-foreground">Performing at {event.title}</p>}
            </div>
          )}

          {/* Heart message */}
          <div className="text-center">
            <Heart className="w-8 h-8 text-red-500 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              Your tip goes directly to the artist
            </p>
          </div>

          {/* Quick tip amounts */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Select Amount</label>
            <div className="grid grid-cols-5 gap-2">
              {tipAmounts.map(amount => (
                <button
                  key={amount}
                  className={`py-3 rounded-lg font-semibold transition-all ${
                    selectedAmount === amount 
                      ? 'bg-primary text-primary-foreground' 
                      : 'bg-secondary text-white hover:bg-secondary/80'
                  }`}
                  onClick={() => {
                    setSelectedAmount(amount);
                    setCustomAmount('');
                  }}
                >
                  ${amount}
                </button>
              ))}
            </div>
          </div>

          {/* Custom amount */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Or Enter Custom Amount</label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input 
                type="number"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => {
                  setCustomAmount(e.target.value);
                  setSelectedAmount(null);
                }}
                className="bg-secondary border-border pl-9"
              />
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="text-sm text-muted-foreground mb-2 block">Add a Message (Optional)</label>
            <textarea
              placeholder="Say something nice..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="w-full bg-secondary border border-border rounded-lg px-4 py-3 text-white placeholder:text-muted-foreground focus:outline-none focus:border-primary resize-none h-20"
            />
          </div>

          {/* Total */}
          {finalAmount > 0 && (
            <Card className="bg-secondary/50 border-border p-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Tip Amount</span>
                <span className="text-2xl font-bold text-accent">${finalAmount.toFixed(2)}</span>
              </div>
            </Card>
          )}

          {/* Send Tip Button */}
          <Button 
            className="w-full bg-accent text-accent-foreground hover:bg-accent/90 h-12 text-lg font-semibold"
            disabled={finalAmount <= 0}
            onClick={handleSendTip}
          >
            <CreditCard className="w-5 h-5 mr-2" />
            Send Tip
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            Tips are processed securely. A small processing fee may apply.
          </p>
        </div>
      </Card>
    </div>
  );
}

export default TipArtistModal;
